import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../frontend/backend/navbar.js";
import Chatbox from "../frontend/backend/chatbox.js";
import HeaderRight from "../frontend/backend/header_right.js";
import LeftSidebar from "../frontend/backend/leftSidebar.js";
import axios from "axios";
import apiConfig from "../apiConfig";

/* ─── inline styles ─────────────────────────────────────────── */
const S = {
  page: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 10,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
    color: "#000000",
    letterSpacing: "-0.3px",
  },
  badge: (color) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    background: color === "green" ? "#14532d" : "#7f1d1d",
    color: color === "green" ? "#111111" : "#000000",
    marginLeft: 8,
  }),
  // ── filter bar ──
  filterBar: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: "14px 18px",
    marginBottom: 16,
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "flex-end",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  filterLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#000000",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  },
  filterInput: {
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    color: "#000000",
    padding: "7px 12px",
    fontSize: 13,
    outline: "none",
    minWidth: 150,
  },
  filterSelect: {
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    color: "#020202",
    padding: "7px 12px",
    fontSize: 13,
    outline: "none",
    cursor: "pointer",
    minWidth: 130,
  },
  btnReset: {
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    color: "#000000",
    padding: "7px 16px",
    fontSize: 13,
    cursor: "pointer",
    alignSelf: "flex-end",
  },
  // ── stat cards ──
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
    marginBottom: 18,
  },
  statCard: (accent) => ({
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${accent}33`,
    borderRadius: 12,
    padding: "14px 18px",
  }),
  statLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: 4,
  },
  statValue: (color) => ({
    fontSize: 20,
    fontWeight: 800,
    color,
    letterSpacing: "-0.5px",
  }),
  statSub: {
    fontSize: 11,
    color: "#475569",
    marginTop: 2,
  },
  // ── table ──
  tableWrap: {
    overflowX: "auto",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.07)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: (sortable) => ({
    padding: "11px 14px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    background: "rgba(0,0,0,0.25)",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    cursor: sortable ? "pointer" : "default",
    whiteSpace: "nowrap",
    userSelect: "none",
  }),
  td: {
    padding: "10px 14px",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  trHover: {
    background: "rgba(255,255,255,0.025)",
  },
  pill: (win) => ({
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    background: win ? "#14532d" : "#7f1d1d",
    color: win ? "#4ade80" : "#f87171",
  }),
  profitCell: (val) => ({
    fontWeight: 700,
    color: val >= 0 ? "#4ade80" : "#f87171",
  }),
  userLink: {
    color: "#38bdf8",
    fontFamily: "monospace",
    fontSize: 12,
    textDecoration: "none",
    background: "rgba(56,189,248,0.08)",
    padding: "2px 8px",
    borderRadius: 6,
    border: "1px solid rgba(56,189,248,0.15)",
  },
  emptyRow: {
    textAlign: "center",
    padding: "40px 0",
    color: "#475569",
    fontSize: 14,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 4px 4px",
    flexWrap: "wrap",
    gap: 8,
  },
  pageInfo: { fontSize: 12, color: "#64748b" },
  pageBtn: (active) => ({
    padding: "5px 11px",
    borderRadius: 7,
    border: "1px solid rgba(255,255,255,0.1)",
    background: active ? "#3b82f6" : "rgba(255,255,255,0.05)",
    color: active ? "#fff" : "#94a3b8",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: active ? 700 : 400,
  }),
};

const PAGE_SIZE = 20; // change this number to show more/fewer rows per page

const LuckyExchangeSummery = () => {
  const { id } = useParams();
  const isSingleUser = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState([]);
  const [totalWin, setTotalWin] = useState(0);
  const [totalBet, setTotalBet] = useState(0);

  // ── filters ──
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");   // all | win | lost
  const [sportFilter, setSportFilter] = useState("all");
  const [backLayFilter, setBackLayFilter] = useState("all"); // all | b | l
  const [search, setSearch] = useState("");                   // match / market name

  // ── sort ──
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  // ── pagination ──
  const [page, setPage] = useState(1);

  const [hoveredRow, setHoveredRow] = useState(null);

  /* ── fetch ────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        const url = isSingleUser
          ? `${apiConfig.baseURL}/api/agent/LuckyExchange/${id}`
          : `${apiConfig.baseURL}/api/agent/LuckyExchange`;

        const response = await axios.get(url, { params });
        setGameData(response.data.data || []);
        setTotalWin(response.data.totalWin || 0);
        setTotalBet(response.data.totalBet || 0);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, startDate, endDate]);

  /* ── helpers ──────────────────────────────────────────────── */
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const options = { hour: "numeric", minute: "numeric", hour12: true };
    const t = date.toLocaleString("en-US", options).toLowerCase();
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = String(date.getFullYear()).slice(-2);
    return `${t}  ${d}.${m}.${y}`;
  };

  const getBet = (item) => (item.bets?.length > 0 ? item.bets[0] : {});
  const amt = (val) => ((val || 0) / 100); // raw amount is stored ×100

  /* ── derived sport list for filter dropdown ───────────────── */
  const sportOptions = useMemo(() => {
    const sports = new Set(gameData.map((r) => getBet(r).sport_name).filter(Boolean));
    return ["all", ...Array.from(sports).sort()];
  }, [gameData]);

  /* ── client-side filtering ────────────────────────────────── */
  const filtered = useMemo(() => {
    return gameData.filter((item) => {
      const bet = getBet(item);
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (sportFilter !== "all" && bet.sport_name !== sportFilter) return false;
      if (backLayFilter !== "all" && bet.backlay !== backLayFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const match = (bet.competitor_name?.join(" ") || "").toLowerCase();
        const market = (bet.market_name || "").toLowerCase();
        const user = (item.user_id || item.ext_player_id || "").toLowerCase();
        if (!match.includes(q) && !market.includes(q) && !user.includes(q)) return false;
      }
      return true;
    });
  }, [gameData, statusFilter, sportFilter, backLayFilter, search]);

  /* ── client-side sort ─────────────────────────────────────── */
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortKey === "amount") { av = amt(a.amount); bv = amt(b.amount); }
      else if (sortKey === "profit") { av = parseFloat(a.trade_profit); bv = parseFloat(b.trade_profit); }
      else { av = new Date(a.createdAt); bv = new Date(b.createdAt); }
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [filtered, sortKey, sortDir]);

  /* ── pagination ───────────────────────────────────────────── */
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
    setPage(1);
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setSportFilter("all");
    setBackLayFilter("all");
    setSearch("");
    setPage(1);
  };

  const arrow = (key) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕";

  /* ── stat computations on filtered set ───────────────────── */
  const filteredWin = filtered.filter((r) => r.status === "win").reduce((s, r) => s + amt(r.amount), 0);
  const filteredLost = filtered.filter((r) => r.status === "lost").reduce((s, r) => s + amt(r.amount), 0);
  const filteredPL = filteredWin - filteredLost;
  const winCount = filtered.filter((r) => r.status === "win").length;
  const lostCount = filtered.filter((r) => r.status === "lost").length;
  const winRate = filtered.length > 0 ? ((winCount / filtered.length) * 100).toFixed(1) : "0.0";

  return (
    <>
      <Navbar />
      <Chatbox />
      <HeaderRight />
      <LeftSidebar />

      <div className="content-body" style={S.page}>
        <div className="container-fluid">
          <div className="card col-lg-12" style={{ background: "transparent", border: "none" }}>
            <div className="container" style={{ maxWidth: "100%", padding: "0 4px" }}>

              {/* ── Header ── */}
              <div style={S.header}>
                <div>
                  <h2 style={S.title}>
                    ⚡ Lucky Exchange
                    <span style={{ color: "#64748b", fontWeight: 400, fontSize: 16 }}>
                      {" — "}{isSingleUser ? "User Report" : "All Users"}
                    </span>
                  </h2>
                </div>
                {isSingleUser && (
                  <Link to={`/exchange-summery/${id}`} className="btn btn-primary shadow btn-xs">
                    ← Exchange History
                  </Link>
                )}
              </div>

              {/* ── Filter Bar ── */}
              <div style={S.filterBar}>
                <div style={S.filterGroup}>
                  <span style={S.filterLabel}>Start Date</span>
                  <input type="date" style={S.filterInput} value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
                </div>
                <div style={S.filterGroup}>
                  <span style={S.filterLabel}>End Date</span>
                  <input type="date" style={S.filterInput} value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
                </div>
                <div style={S.filterGroup}>
                  <span style={S.filterLabel}>Status</span>
                  <select style={S.filterSelect} value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                    <option value="all">All</option>
                    <option value="win">Win</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                <div style={S.filterGroup}>
                  <span style={S.filterLabel}>Sport</span>
                  <select style={S.filterSelect} value={sportFilter}
                    onChange={(e) => { setSportFilter(e.target.value); setPage(1); }}>
                    {sportOptions.map((s) => (
                      <option key={s} value={s}>{s === "all" ? "All Sports" : s}</option>
                    ))}
                  </select>
                </div>
                <div style={S.filterGroup}>
                  <span style={S.filterLabel}>Back / Lay</span>
                  <select style={S.filterSelect} value={backLayFilter}
                    onChange={(e) => { setBackLayFilter(e.target.value); setPage(1); }}>
                    <option value="all">All</option>
                    <option value="b">Back</option>
                    <option value="l">Lay</option>
                  </select>
                </div>
                <div style={S.filterGroup}>
                  <span style={S.filterLabel}>Search</span>
                  <input type="text" placeholder="Match, market, user…" style={S.filterInput}
                    value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                </div>
                <button style={S.btnReset} onClick={resetFilters}>✕ Reset</button>
              </div>

              {/* ── Stat Cards ── */}
              <div style={S.statsRow}>
                <div style={S.statCard("#22c55e")}>
                  <div style={S.statLabel}>Profit / Loss</div>
                  <div style={S.statValue(filteredPL >= 0 ? "#4ade80" : "#f87171")}>
                    {filteredPL.toLocaleString()}
                  </div>
                  <div style={S.statSub}>BDT (filtered)</div>
                </div>
                <div style={S.statCard("#3b82f6")}>
                  <div style={S.statLabel}>Total Win Amount</div>
                  <div style={S.statValue("#4ade80")}>{filteredWin.toLocaleString()}</div>
                  <div style={S.statSub}>{winCount} bets won</div>
                </div>
                <div style={S.statCard("#ef4444")}>
                  <div style={S.statLabel}>Total Lost Amount</div>
                  <div style={S.statValue("#f87171")}>{filteredLost.toLocaleString()}</div>
                  <div style={S.statSub}>{lostCount} bets lost</div>
                </div>
                <div style={S.statCard("#a855f7")}>
                  <div style={S.statLabel}>Win Rate</div>
                  <div style={S.statValue("#c084fc")}>{winRate}%</div>
                  <div style={S.statSub}>{filtered.length} total bets</div>
                </div>
                <div style={S.statCard("#f59e0b")}>
                  <div style={S.statLabel}>Showing</div>
                  <div style={S.statValue("#fbbf24")}>{filtered.length}</div>
                  <div style={S.statSub}>of {gameData.length} records</div>
                </div>
              </div>

              {/* ── Table ── */}
              {loading ? (
                <div style={{ textAlign: "center", padding: 40, color: "#475569" }}>
                  Loading…
                </div>
              ) : (
                <>
                  <div style={S.tableWrap}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th(true)} onClick={() => handleSort("createdAt")}>
                            Date{arrow("createdAt")}
                          </th>
                          {!isSingleUser && <th style={S.th(false)}>User</th>}
                          <th style={S.th(true)} onClick={() => handleSort("amount")}>
                            Amount{arrow("amount")}
                          </th>
                          <th style={S.th(false)}>Status</th>
                          <th style={S.th(false)}>Sport</th>
                          <th style={S.th(false)}>Match</th>
                          <th style={S.th(false)}>Market</th>
                          <th style={S.th(false)}>Outcome</th>
                          <th style={S.th(false)}>Odds</th>
                          <th style={S.th(false)}>B/L</th>
                          <th style={S.th(true)} onClick={() => handleSort("profit")}>
                            Trade P/L{arrow("profit")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.length > 0 ? (
                          paginated.map((item, index) => {
                            const bet = getBet(item);
                            const isWin = item.status === "win";
                            const profit = parseFloat(item.trade_profit) || 0;
                            return (
                              <tr key={index}
                                onMouseEnter={() => setHoveredRow(index)}
                                onMouseLeave={() => setHoveredRow(null)}
                                style={hoveredRow === index ? S.trHover : {}}>
                                <td style={S.td}>
                                  <span style={{ color: "#94a3b8", fontSize: 12 }}>
                                    {formatDate(item.createdAt)}
                                  </span>
                                </td>
                                {!isSingleUser && (
                                  <td style={S.td}>
                                    <Link
                                      to={`/lucky-exchange-summery/${item.ext_player_id}`}
                                      style={S.userLink}>
                                      {item.user_id || item.mobile || (item.ext_player_id || "").slice(-8)}
                                    </Link>
                                  </td>
                                )}
                                <td style={{ ...S.td, fontWeight: 700, color: "#e2e8f0" }}>
                                  {amt(item.amount).toLocaleString()}
                                </td>
                                <td style={S.td}>
                                  <span style={S.pill(isWin)}>{isWin ? "Win" : "Lost"}</span>
                                </td>
                                <td style={S.td}>
                                  <span style={{
                                    fontSize: 11, fontWeight: 600,
                                    color: bet.sport_name === "Cricket" ? "#34d399"
                                         : bet.sport_name === "Soccer"  ? "#60a5fa" : "#a78bfa"
                                  }}>
                                    {bet.sport_name || "-"}
                                  </span>
                                </td>
                                <td style={{ ...S.td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {bet.competitor_name?.join(" vs ") || "-"}
                                </td>
                                <td style={{ ...S.td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", color: "#94a3b8" }}>
                                  {bet.market_name || "-"}
                                </td>
                                <td style={{ ...S.td, color: "#94a3b8" }}>{bet.outcome_name || "-"}</td>
                                <td style={{ ...S.td, fontWeight: 700, color: "#fbbf24" }}>{bet.odds || "-"}</td>
                                <td style={S.td}>
                                  <span style={{
                                    fontSize: 11, fontWeight: 700,
                                    color: bet.backlay === "b" ? "#38bdf8" : "#f472b6"
                                  }}>
                                    {bet.backlay === "b" ? "BACK" : bet.backlay === "l" ? "LAY" : "-"}
                                  </span>
                                </td>
                                <td style={{ ...S.td, ...S.profitCell(profit) }}>
                                  {profit >= 0 ? "+" : ""}{profit.toLocaleString()}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={isSingleUser ? 10 : 11} style={S.emptyRow}>
                              No records found for the selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Pagination ── */}
                  {sorted.length > 0 && (
                    <div style={S.pagination}>
                      <span style={S.pageInfo}>
                        Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}
                      </span>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button style={S.pageBtn(false)} onClick={() => setPage(1)} disabled={page === 1}>«</button>
                        <button style={S.pageBtn(false)} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const p = Math.max(1, Math.min(page - 2 + i, totalPages - 4 + i));
                          return (
                            <button key={p} style={S.pageBtn(p === page)} onClick={() => setPage(p)}>{p}</button>
                          );
                        })}
                        <button style={S.pageBtn(false)} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
                        <button style={S.pageBtn(false)} onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                      </div>
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LuckyExchangeSummery;
